import {
  MATERIAL_DEFAULTS,
  MATERIAL_LABELS,
} from '../../../domain/data-collection/constants';
import { newBooth } from '../../../domain/data-collection/defaults';
import { cleanNumericInput, formatNumericInput } from '../../../utils/numberInput';
import { readSdxValue } from '../../../utils/sdxHelpers';
import {
  Badge,
  SdxField,
  ChoiceCard,
  ERROR_TEXT,
  Hint,
  SectionCard,
} from '../UIComponents';

const BRAND = '#004A53';
const INTERACTION = '#026F6D';
const GREEN_SURFACE = '#f0f7f6';
const GREEN_BORDER = '#b7cfcc';
const MUTED = '#64748b';

const BOOTH_TYPES = [
  'Standard-Kabine',
  'Kompaktkabine',
  'Grosskabine',
  'Vorbereitung & Spot-Repair',
];

const BOOTH_HEATING = [
  'Ja',
  'Nein, in Gebäudeheizung',
];

const BURNER_TYPES = [
  'Erdgas',
  'Heizöl',
  'Flüssiggas (LPG)',
  'Holz/Pellets',
  'Wärmepumpe',
  'Andere',
];

const BOOTH_ENERGY_UNIT_BY_BURNER = {
  Erdgas: 'kwh',
  'Heizöl': 'liter',
  'Flüssiggas (LPG)': 'liter',
  'Holz/Pellets': 'tonnen',
  Wärmepumpe: 'kwh',
  Andere: 'kwh',
};

const BOOTH_ENERGY_UNITS = [
  { value: 'kwh', label: 'kWh' },
  { value: 'liter', label: 'Liter' },
  { value: 'tonnen', label: 'Tonnen' },
  { value: 'chf', label: 'CHF' },
];

const DRYING_TYPES = [
  'Konventionell / Warmluft',
  'Infrarot',
  'Lufttrocknend',
];

const EFFICIENCY_OPTIONS = [
  'WRG',
  'Sparschaltung',
  'Frequenzumformer',
  'Keine',
  'Weiss nicht',
];

const EXCLUSIVE_EFFICIENCY_OPTIONS = [
  'Keine',
  'Weiss nicht',
];

const REPAIR_TYPES = [
  'Spot Repair',
  'Blech / Richten',
  'Drücken',
  'Stossstangen',
  'Glas',
  'Strukturschäden',
];

function BoothCard({
  booth,
  index,
  onUpdate,
  onRemove,
  isReadOnly,
  canRemove,
  fieldErrors,
}) {
  const heatedNo =
    booth.separatelyHeated === 'Nein, in Gebäudeheizung';

  const heatedYes =
    booth.separatelyHeated === 'Ja';

  function toggleEfficiency(tech) {
    const current = booth.efficiencyTech || [];

    if (EXCLUSIVE_EFFICIENCY_OPTIONS.includes(tech)) {
      onUpdate(
        'efficiencyTech',
        current.includes(tech) ? [] : [tech]
      );

      return;
    }

    if (current.includes(tech)) {
      onUpdate(
        'efficiencyTech',
        current.filter(item => item !== tech)
      );

      return;
    }

    const hasExclusive = current.some(item =>
      EXCLUSIVE_EFFICIENCY_OPTIONS.includes(item)
    );

    const next = hasExclusive
      ? current.filter(item => !EXCLUSIVE_EFFICIENCY_OPTIONS.includes(item))
      : [...current];

    onUpdate(
      'efficiencyTech',
      [...next, tech]
    );
  }

  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        padding: '1.25rem',
        marginBottom: 16,
        background: '#f8fafc',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h3
          className="h3"
          style={{ margin: 0, fontSize: 15, color: BRAND }}
        >
          Lackierkabine {index + 1}
        </h3>

        {canRemove && (
          <sdx-button
            theme="cancel"
            variant="ghost"
            label="Entfernen"
            onClick={onRemove}
            disabled={isReadOnly || undefined}
          ></sdx-button>
        )}
      </div>

      {/* Photo */}
      <div style={{ marginBottom: 16, opacity: 0.55 }}>
        <label
          className="label"
          style={{ display: 'block', marginBottom: 6 }}
        >
          Foto Typenschild
          <span
            style={{
              marginLeft: 6,
              fontSize: 11,
              padding: '1px 7px',
              borderRadius: 999,
              background: '#f1f5f9',
              color: '#64748b',
              fontWeight: 600,
            }}
          >
            noch nicht verfügbar
          </span>
        </label>

        <input
          type="file"
          accept="image/*"
          disabled
          id={`booth-photo-${booth.id}`}
          style={{ display: 'none' }}
        />

        <label
          htmlFor={`booth-photo-${booth.id}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 14px',
            border: '1px dashed #cbd5e1',
            borderRadius: 8,
            cursor: 'default',
            fontSize: 14,
            color: '#64748b',
            background: 'white',
          }}
        >
          📷 Foto auswählen…
        </label>

        <Hint>
          Automatisches Auslesen von Typenschild-Fotos ist noch nicht
          verfügbar. Bitte die Kabinendaten unten manuell erfassen.
        </Hint>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <SdxField
          required={false}
          valid={fieldErrors?.[`paintBooths.${index}.buildYear`] ? 'false' : undefined}
          validation-message={fieldErrors?.[`paintBooths.${index}.buildYear`] || undefined}
          label="Baujahr"
          type="text"
          inputmode="numeric"
          value={cleanNumericInput(booth.buildYear ?? '')}
          onInput={event =>
            onUpdate(
              'buildYear',
              cleanNumericInput(event.target.value)
            )
          }
          disabled={isReadOnly || undefined}
          placeholder="z. B. 2012"
        ></SdxField>

        <div>
          <SdxField as="sdx-select"
            valid={fieldErrors?.[`paintBooths.${index}.type`] ? 'false' : undefined}
            validation-message={fieldErrors?.[`paintBooths.${index}.type`] || undefined}
            label="Typ"
            placeholder="Auswählen…"
            onInput={event =>
              onUpdate('type', readSdxValue(event))
            }
            disabled={isReadOnly || undefined}
          >
            {BOOTH_TYPES.map(type => (
              <sdx-select-option
                key={type}
                value={type}
                selected={booth.type === type}
              >
                {type}
              </sdx-select-option>
            ))}
          </SdxField>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label
          className="label"
          style={{ display: 'block', marginBottom: 8 }}
        >
          Separat beheizt <Badge type="optional" />
        </label>

        <div
          style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          {BOOTH_HEATING.map(option => (
            <ChoiceCard
              key={option}
              label={option}
              selected={
                booth.separatelyHeated === option
              }
              onClick={() => {
                onUpdate('separatelyHeated', option);

                if (
                  option ===
                  'Nein, in Gebäudeheizung'
                ) {
                  onUpdate('burnerEnergy', '');
                  onUpdate('energyConsumption', '');
                  onUpdate(
                    'energyConsumptionUnit',
                    ''
                  );
                  return;
                }

                if (option === 'Ja') {
                  const nextUnit =
                    booth.burnerEnergy
                      ? BOOTH_ENERGY_UNIT_BY_BURNER[
                          booth.burnerEnergy
                        ] ?? 'kwh'
                      : 'kwh';

                  onUpdate(
                    'energyConsumptionUnit',
                    nextUnit
                  );
                }
              }}
              disabled={isReadOnly}
            />
          ))}
        </div>
      </div>

      {heatedYes && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <SdxField as="sdx-select"
            label="Brenner-Energie"
            placeholder="Auswählen…"
            onInput={event => {
              const nextBurnerEnergy = readSdxValue(event);
              const nextUnit =
                BOOTH_ENERGY_UNIT_BY_BURNER[nextBurnerEnergy] ??
                booth.energyConsumptionUnit;

              onUpdate('burnerEnergy', nextBurnerEnergy);
              onUpdate('energyConsumptionUnit', nextUnit);
            }}
            disabled={
              isReadOnly || heatedNo || undefined
            }
          >
            {BURNER_TYPES.map(type => (
              <sdx-select-option
                key={type}
                value={type}
                selected={
                  booth.burnerEnergy === type
                }
              >
                {type}
              </sdx-select-option>
            ))}
          </SdxField>

          {(() => {
            const allowedUnit = booth.burnerEnergy
              ? BOOTH_ENERGY_UNIT_BY_BURNER[booth.burnerEnergy] ?? null
              : null;

            return (
              <SdxField as="sdx-select"
                label="Einheit"
                placeholder="Auswählen…"
                onInput={event =>
                  onUpdate(
                    'energyConsumptionUnit',
                    readSdxValue(event)
                  )
                }
                disabled={
                  isReadOnly || heatedNo || undefined
                }
              >
                {BOOTH_ENERGY_UNITS.map(unit => {
                  const optionDisabled =
                    !!allowedUnit && unit.value !== allowedUnit;

                  return (
                    <sdx-select-option
                      key={unit.value}
                      value={unit.value}
                      selected={
                        booth.energyConsumptionUnit ===
                        unit.value
                      }
                      disabled={
                        optionDisabled || undefined
                      }
                    >
                      {unit.label}
                    </sdx-select-option>
                  );
                })}
              </SdxField>
            );
          })()}

          <SdxField
            label="Energieverbrauch Kabine/Jahr"
            type="text"
            inputmode="decimal"
            value={formatNumericInput(booth.energyConsumption ?? '')}
            onInput={event =>
              onUpdate(
                'energyConsumption',
                cleanNumericInput(event.target.value)
              )
            }
            disabled={
              isReadOnly || heatedNo || undefined
            }
            placeholder="z. B. 12'000"
          ></SdxField>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label
          className="label"
          style={{ display: 'block', marginBottom: 8 }}
        >
          Effizienztechnik
          <span
            style={{
              marginLeft: 6,
              fontSize: 11,
              padding: '1px 7px',
              borderRadius: 999,
              background: GREEN_SURFACE,
              color: BRAND,
              fontWeight: 600,
            }}
          >
            optional
          </span>
        </label>

        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          {EFFICIENCY_OPTIONS.map(option => (
            <ChoiceCard
              key={option}
              label={option}
              selected={(
                booth.efficiencyTech || []
              ).includes(option)}
              onClick={() =>
                toggleEfficiency(option)
              }
              disabled={isReadOnly}
              multiSelect
            />
          ))}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 16,
        }}
      >
        <SdxField as="sdx-select"
          valid={fieldErrors?.[`paintBooths.${index}.dryingType`] ? 'false' : undefined}
          validation-message={fieldErrors?.[`paintBooths.${index}.dryingType`] || undefined}
          label="Trocknungsart"
          placeholder="Auswählen…"
          onInput={event =>
            onUpdate(
              'dryingType',
              readSdxValue(event)
            )
          }
          disabled={isReadOnly || undefined}
        >
          {DRYING_TYPES.map(type => (
            <sdx-select-option
              key={type}
              value={type}
              selected={booth.dryingType === type}
            >
              {type}
            </sdx-select-option>
          ))}
        </SdxField>

        <SdxField
          required={false}
          valid={fieldErrors?.[`paintBooths.${index}.dryingTemp`] ? 'false' : undefined}
          validation-message={fieldErrors?.[`paintBooths.${index}.dryingTemp`] || undefined}
          label="Ø Trocknungstemperatur (°C)"
          type="text"
          inputmode="decimal"
          value={formatNumericInput(booth.dryingTemp ?? '')}
          onInput={event =>
            onUpdate(
              'dryingTemp',
              cleanNumericInput(event.target.value)
            )
          }
          disabled={isReadOnly || undefined}
          placeholder="z. B. 60"
        ></SdxField>

        <SdxField
          required={false}
          valid={fieldErrors?.[`paintBooths.${index}.dryingTime`] ? 'false' : undefined}
          validation-message={fieldErrors?.[`paintBooths.${index}.dryingTime`] || undefined}
          label="Ø Trocknungszeit (min)"
          type="text"
          inputmode="decimal"
          value={formatNumericInput(booth.dryingTime ?? '')}
          onInput={event =>
            onUpdate(
              'dryingTime',
              cleanNumericInput(event.target.value)
            )
          }
          disabled={isReadOnly || undefined}
          placeholder="z. B. 45"
        ></SdxField>
      </div>
    </div>
  );
}

function getAirDryingHint(share) {
  const value = parseFloat(share) || 0;

  if (value >= 80) {
    return {
      text: 'Sehr hoher Anteil lufttrocknender Lacke.',
      type: 'success',
    };
  }

  if (value >= 50) {
    return {
      text: 'Der Lackanteil ist mehrheitlich lufttrocknend.',
      type: 'info',
    };
  }

  if (value > 0) {
    return {
      text: 'Lufttrocknender Lack wird teilweise eingesetzt.',
      type: 'info',
    };
  }

  return {
    text: 'Aktuell wird kein lufttrocknender Lack eingesetzt.',
    type: 'warning',
  };
}

function isCarbonPulseImported(formData, field) {
  return (
    Array.isArray(formData.carbonPulseImportedFields) &&
    formData.carbonPulseImportedFields.includes(field)
  );
}

export default function PaintRepairStep({
  formData,
  setField,
  setFormData,
  isReadOnly,
  fieldErrors,
  operationType,
}) {
  const paintLitersImported = isCarbonPulseImported(
    formData,
    'paintLiters'
  );
  const repairsPerYearImported = isCarbonPulseImported(
    formData,
    'repairsPerYear'
  );
  function updateBooth(index, key, value) {
    setFormData(previous => {
      const booths = [...previous.paintBooths];

      booths[index] = {
        ...booths[index],
        [key]: value,
      };

      return {
        ...previous,
        paintBooths: booths,
      };
    });
  }

  function addBooth() {
    setFormData(previous => ({
      ...previous,
      paintBooths: [
        ...previous.paintBooths,
        newBooth(),
      ],
    }));
  }

  function removeBooth(index) {
    setFormData(previous => ({
      ...previous,
      paintBooths:
        previous.paintBooths.filter(
          (_, boothIndex) =>
            boothIndex !== index
        ),
    }));
  }

  function toggleRepairType(type) {
    const currentTypes =
      formData.repairTypes || [];

    const isSelected =
      currentTypes.includes(type);

    if (isSelected) {
      setField(
        'repairTypes',
        currentTypes.filter(
          currentType =>
            currentType !== type
        )
      );

      const nextShares = {
        ...(formData.repairTypeShares || {}),
      };

      delete nextShares[type];

      setField(
        'repairTypeShares',
        nextShares
      );

      return;
    }

    setField(
      'repairTypes',
      [...currentTypes, type]
    );

    setField('repairTypeShares', {
      ...(formData.repairTypeShares || {}),
      [type]:
        formData.repairTypeShares?.[type] ??
        '',
    });
  }

  function updateMaterial(key, value) {
    setFormData(previous => ({
      ...previous,
      materials: {
        ...previous.materials,
        [key]: value,
      },
    }));
  }

  const materialSum = Object.values(
    formData.materials || {}
  ).reduce(
    (sum, value) =>
      sum + (parseFloat(value) || 0),
    0
  );

  const materialSumOk =
    Math.abs(materialSum - 100) < 0.01;

  const airDryingHint = getAirDryingHint(
    formData.airDryingPaintShare
  );

  const conventionalPaintShare = Math.max(
    0,
    100 -
      (parseFloat(
        formData.airDryingPaintShare
      ) || 0)
  );

  return (
    <div>
      {/* Lackierkabinen */}
      <SectionCard title="Lackierkabinen" badge="Pflicht">
        {formData.paintBooths.map(
          (booth, index) => (
            <BoothCard
              fieldErrors={fieldErrors}
              key={booth.id}
              booth={booth}
              index={index}
              onUpdate={(key, value) =>
                updateBooth(
                  index,
                  key,
                  value
                )
              }
              onRemove={() =>
                removeBooth(index)
              }
              isReadOnly={isReadOnly}
              canRemove={
                formData.paintBooths.length > 1
              }
            />
          )
        )}

        <sdx-button
          theme="secondary"
          label="+ Weitere Kabine hinzufügen"
          onClick={addBooth}
          disabled={isReadOnly || undefined}
        ></sdx-button>
      </SectionCard>

      {/* Lack-Anteile */}
      <SectionCard title="Lack-Anteile" badge="Pflicht">
        {paintLitersImported && (
          <Hint type="success">
            Aus der Carbonpulse-Vorab-Checkliste übernommen. Werte können hier korrigiert werden.
          </Hint>
        )}

        <div style={{ opacity: 1 }}>
          <SdxField
            label="Eingekaufter Lack (Liter/Jahr)"
            type="text"
            inputmode="decimal"
            value={formatNumericInput(formData.paintLiters ?? '')}
            onInput={event =>
              setField(
                'paintLiters',
                cleanNumericInput(event.target.value)
              )
            }
            disabled={
              isReadOnly || undefined
            }
            placeholder="z. B. 800"
            valid={
              fieldErrors?.paintLiters
                ? 'false'
                : undefined
            }
            validation-message={
              fieldErrors?.paintLiters ||
              undefined
            }
          ></SdxField>
        </div>

        <div style={{ marginTop: 20 }}>
          <label
            className="label"
            style={{
              display: 'block',
              marginBottom: 8,
            }}
          >
            Anteil lufttrocknender Lacke (%)
          </label>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <input
              type="range"
              min={0}
              max={100}
              value={
                formData.airDryingPaintShare ??
                0
              }
              onChange={event =>
                setField(
                  'airDryingPaintShare',
                  Number(event.target.value)
                )
              }
              disabled={isReadOnly}
              style={{
                flex: 1,
                accentColor:
                  INTERACTION,
                cursor: isReadOnly
                  ? 'default'
                  : 'pointer',
              }}
            />

            <sdx-input
              type="number"
              value={
                formData.airDryingPaintShare ??
                ''
              }
              onInput={event =>
                setField(
                  'airDryingPaintShare',
                  Math.min(
                    100,
                    Math.max(
                      0,
                      Number(
                        event.target.value
                      )
                    )
                  )
                )
              }
              disabled={isReadOnly || undefined}
              style={{ width: '80px' }}
            ></sdx-input>

            <span
              style={{
                fontSize: 14,
                color: MUTED,
              }}
            >
              %
            </span>
          </div>

          {fieldErrors?.airDryingPaintShare && (
            <span style={ERROR_TEXT}>
              {
                fieldErrors.airDryingPaintShare
              }
            </span>
          )}
        </div>

        <div
          style={{
            marginTop: 16,
            display: 'flex',
            gap: 16,
            padding: 14,
            background: '#f8fafc',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 12,
                color: MUTED,
                marginBottom: 4,
              }}
            >
              Lufttrocknender Lack-Anteil
            </div>

            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: INTERACTION,
              }}
            >
              {
                formData.airDryingPaintShare ??
                0
              }{' '}
              %
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 12,
                color: MUTED,
                marginBottom: 4,
              }}
            >
              Konventioneller Lack-Anteil
              (abgeleitet)
            </div>

            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: '#1e293b',
              }}
            >
              {conventionalPaintShare} %
            </div>
          </div>
        </div>

        <Hint type={airDryingHint.type}>
          {airDryingHint.text}
        </Hint>
      </SectionCard>

      {/* Verbrauchsmaterial */}
      <SectionCard title="Verbrauchsmaterial" badge="optional">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16,
          }}
        >
          <span
            style={{
              fontSize: 13,
              color: MUTED,
            }}
          >
            Basierend auf Betriebstyp:
          </span>

          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: '2px 10px',
              borderRadius: 999,
              background: GREEN_SURFACE,
              color: BRAND,
            }}
          >
            {operationType}
          </span>

          <div style={{ marginLeft: 'auto' }}>
            <sdx-button
              theme="secondary"
              label="Defaults zurücksetzen"
              onClick={() =>
                setFormData(previous => ({
                  ...previous,
                  materials: {
                    ...MATERIAL_DEFAULTS[
                      operationType
                    ],
                  },
                }))
              }
              disabled={isReadOnly || undefined}
            ></sdx-button>
          </div>
        </div>

        {Object.entries(
          MATERIAL_LABELS
        ).map(([key, label]) => (
          <div
            key={key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                minWidth: 200,
                fontSize: 14,
                color: '#374151',
              }}
            >
              {label}
            </div>

            <input
              type="range"
              min={0}
              max={100}
              value={
                formData.materials?.[key] ||
                0
              }
              onChange={event =>
                updateMaterial(
                  key,
                  Number(event.target.value)
                )
              }
              disabled={isReadOnly}
              style={{
                flex: 1,
                accentColor:
                  INTERACTION,
                cursor: isReadOnly
                  ? 'default'
                  : 'pointer',
              }}
            />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <sdx-input
                type="number"
                value={
                  formData.materials?.[key] ??
                  0
                }
                onInput={event =>
                  updateMaterial(
                    key,
                    Number(event.target.value)
                  )
                }
                disabled={
                  isReadOnly || undefined
                }
                style={{ width: '72px' }}
              ></sdx-input>

              <span
                style={{
                  fontSize: 14,
                  color: MUTED,
                }}
              >
                %
              </span>
            </div>
          </div>
        ))}

        <div
          style={{
            padding: '10px 14px',
            borderRadius: 8,
            background: materialSumOk
              ? GREEN_SURFACE
              : '#fffbeb',
            border: `1px solid ${
              materialSumOk
                ? GREEN_BORDER
                : '#fde68a'
            }`,
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <span
            style={{
              fontWeight: 600,
              color: materialSumOk
                ? BRAND
                : '#92400e',
            }}
          >
            Summe: {materialSum.toFixed(1)} %
          </span>

          {!materialSumOk && (
            <span
              style={{
                fontSize: 13,
                color: '#d97706',
              }}
            >
              Die Summe muss 100% betragen
            </span>
          )}
        </div>

      </SectionCard>

      {/* Reparaturen */}
      <SectionCard title="Reparaturen" badge="Pflicht">
        {repairsPerYearImported && (
          <Hint type="success">
            Aus der Carbonpulse-Vorab-Checkliste übernommen. Werte können hier korrigiert werden.
          </Hint>
        )}

        <div style={{ opacity: 1 }}>
          <SdxField
            label="Reparaturen pro Jahr"
            type="text"
            inputmode="decimal"
            value={formatNumericInput(formData.repairsPerYear ?? '')}
            onInput={event =>
              setField(
                'repairsPerYear',
                cleanNumericInput(event.target.value)
              )
            }
            disabled={
              isReadOnly || undefined
            }
            placeholder="z. B. 850"
            valid={
              fieldErrors?.repairsPerYear
                ? 'false'
                : undefined
            }
            validation-message={
              fieldErrors?.repairsPerYear ||
              undefined
            }
          ></SdxField>

          <p
            style={{
              fontSize: 12,
              color: '#94a3b8',
              marginTop: 4,
            }}
          >
            Anzahl abgeschlossener
            Reparaturaufträge im letzten
            Geschäftsjahr.
          </p>
        </div>

        <div style={{ marginTop: 16 }}>
          <label
            className="label"
            style={{
              display: 'block',
              marginBottom: 8,
            }}
          >
            Reparaturarten
          </label>

          <div
            style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            {REPAIR_TYPES.map(type => (
              <ChoiceCard
                key={type}
                label={type}
                selected={(
                  formData.repairTypes || []
                ).includes(type)}
                onClick={() =>
                  toggleRepairType(type)
                }
                disabled={isReadOnly}
                multiSelect
              />
            ))}
          </div>

          {fieldErrors?.repairTypes && (
            <span style={ERROR_TEXT}>
              {fieldErrors.repairTypes}
            </span>
          )}
        </div>

        {(formData.repairTypes || []).map(
          type => {
            const share =
              formData.repairTypeShares?.[
                type
              ] ?? '';

            return (
              <div
                key={type}
                style={{
                  marginTop: 16,
                  padding: 14,
                  border:
                    '1px solid #e2e8f0',
                  borderRadius: 8,
                  background: '#f8fafc',
                }}
              >
                <label
                  className="label"
                  style={{
                    display: 'block',
                    marginBottom: 8,
                  }}
                >
                  Reparaturquote {type}:{' '}
                  {share} %
                </label>

                <p
                  style={{
                    fontSize: 13,
                    color: MUTED,
                    marginTop: 0,
                  }}
                >
                  Anteil der Fälle, die
                  repariert statt ersetzt
                  werden.
                </p>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={share === '' ? 0 : share}
                    onChange={event =>
                      setField(
                        'repairTypeShares',
                        {
                          ...(formData.repairTypeShares ||
                            {}),
                          [type]: Number(
                            event.target.value
                          ),
                        }
                      )
                    }
                    disabled={isReadOnly}
                    style={{
                      flex: 1,
                      accentColor:
                        INTERACTION,
                      cursor: isReadOnly
                        ? 'default'
                        : 'pointer',
                    }}
                  />

                  <sdx-input
                    type="number"
                    value={share}
                    onInput={event =>
                      setField(
                        'repairTypeShares',
                        {
                          ...(formData.repairTypeShares ||
                            {}),
                          [type]: event.target.value === '' ? '' : Math.min(
                            100,
                            Math.max(
                              0,
                              Number(
                                event.target
                                  .value
                              )
                            )
                          ),
                        }
                      )
                    }
                    disabled={
                      isReadOnly || undefined
                    }
                    style={{ width: '80px' }}
                  ></sdx-input>

                  <span style={{ fontSize: 14 }}>
                    %
                  </span>
                </div>

                {fieldErrors?.[
                  `repairTypeShares.${type}`
                ] && (
                  <span style={ERROR_TEXT}>
                    {
                      fieldErrors[
                        `repairTypeShares.${type}`
                      ]
                    }
                  </span>
                )}
              </div>
            );
          }
        )}
      </SectionCard>

      {/* Ersatzteile */}
      <SectionCard
        title="Ersatzteile & Recyclingteile"
        badge="optional"
      >
        <label
          className="label"
          style={{
            display: 'block',
            marginBottom: 8,
          }}
        >
          Tendenz Ersatzteile
        </label>

        <div
          style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          {[
            'Überwiegend neu',
            'Gemischt',
            'Neu aufbereitet',
          ].map(option => (
            <ChoiceCard
              key={option}
              label={option}
              selected={
                formData.sparePartsTendency ===
                option
              }
              onClick={() =>
                setField(
                  'sparePartsTendency',
                  formData.sparePartsTendency ===
                    option
                    ? ''
                    : option
                )
              }
              disabled={isReadOnly}
            />
          ))}
        </div>
{/*
        <label
          className="label"
          style={{
            display: 'block',
            marginBottom: 8,
          }}
        >
          Anteil Recycling-/wiederaufbereitete
          Teile: {formData.recyclingPartsShare} %
        </label>
        <input
          type="range"
          min={0}
          max={100}
          value={
            formData.recyclingPartsShare
          }
          onChange={event =>
            setField(
              'recyclingPartsShare',
              Number(event.target.value)
            )
          }
          disabled={isReadOnly}
          style={{
            width: '100%',
            accentColor: INTERACTION,
            cursor: isReadOnly
              ? 'default'
              : 'pointer',
            marginBottom: 16,
          }}
        />*/}

        <sdx-input
          label="Eingekaufte Ersatzteile (CHF/Jahr)"
          type="text"
          inputmode="decimal"
          value={formatNumericInput(formData.sparePartsValueCHF ?? '')}
          onInput={event =>
            setField(
              'sparePartsValueCHF',
              cleanNumericInput(event.target.value)
            )
          }
          disabled={isReadOnly || undefined}
          placeholder="z. B. 150'000"
          valid={fieldErrors?.sparePartsValueCHF ? 'false' : undefined}
          validation-message={fieldErrors?.sparePartsValueCHF || undefined}
        ></sdx-input>
      </SectionCard>
    </div>
  );
}
